import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save, Timelapse } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import DetailHeader from '../components/DetailHeader';
import { SUPPORTED_CURRENCY_CODES } from '../constants/currency';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import {
  createMinutePackAdmin,
  fetchMinutePackAdmin,
  updateMinutePackAdmin,
} from '../services/subscriptions';
import type { CurrencyCode } from '../types';

type MinutePackType = 'ai' | 'telephony';

const defaultForm: {
  type: MinutePackType;
  minutesQuantity: string;
  priceAmount: string;
  priceCurrency: CurrencyCode;
  isActive: boolean;
} = {
  type: 'ai',
  minutesQuantity: '',
  priceAmount: '',
  priceCurrency: 'BRL',
  isActive: true,
};

export default function MinutePackFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new' || id === 'undefined';
  const { formatCurrency } = useNumberFormat();
  usePageTitle(isNew ? t('pages:minutePacks.new') : t('pages:minutePacks.editTitle'));

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew || !id) return;
    const loadPack = async () => {
      try {
        setLoading(true);
        const pack = await fetchMinutePackAdmin(id as string);
        const priceCurrency = SUPPORTED_CURRENCY_CODES.includes(pack.priceCurrency as CurrencyCode)
          ? (pack.priceCurrency as CurrencyCode)
          : 'BRL';
        setForm({
          type: pack.type,
          minutesQuantity: String(pack.minutesQuantity ?? ''),
          priceAmount: String(pack.priceAmount ?? ''),
          priceCurrency,
          isActive: pack.isActive,
        });
      } catch (err) {
        console.error('Error loading minute pack:', err);
        setError(t('pages:minutePacks.errors.load'));
      } finally {
        setLoading(false);
      }
    };
    loadPack();
  }, [id, isNew, t]);

  const breadcrumbs = useMemo(
    () => [
      { label: t('common:menu.dashboard'), to: '/' },
      { label: t('pages:minutePacks.title'), to: '/subscriptions/minute-packs' },
      { label: isNew ? t('pages:minutePacks.new') : t('pages:minutePacks.editTitle') },
    ],
    [isNew, t]
  );

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        type: form.type,
        minutes_quantity: Number(form.minutesQuantity || 0),
        price_amount: form.priceAmount ? Number(form.priceAmount) : undefined,
        price_currency: form.priceCurrency,
        is_active: form.isActive,
      };

      if (isNew) {
        await createMinutePackAdmin(payload);
      } else {
        await updateMinutePackAdmin(id as string, payload);
      }
      navigate('/subscriptions/minute-packs');
    } catch (err) {
      console.error('Error saving minute pack:', err);
      setError(t('pages:minutePacks.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <DetailHeader
          sectionIcon={<Timelapse />}
          title={t('pages:minutePacks.title')}
          breadcrumbs={breadcrumbs}
        />
        <Typography color="text.secondary">{t('common:common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        sectionIcon={<Timelapse />}
        title={isNew ? t('pages:minutePacks.new') : t('pages:minutePacks.editTitle')}
        breadcrumbs={breadcrumbs}
        actions={(
          <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        )}
      />
      <Card>
        <CardContent>
          <Stack spacing={3}>
            {error && <Typography color="error">{error}</Typography>}
            <FormControl fullWidth>
              <InputLabel>{t('pages:minutePacks.fields.type')}</InputLabel>
              <Select
                value={form.type}
                label={t('pages:minutePacks.fields.type')}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, type: event.target.value as MinutePackType }))
                }
              >
                <MenuItem value="ai">{t('pages:subscription.packTypes.ai')}</MenuItem>
                <MenuItem value="telephony">{t('pages:subscription.packTypes.telephony')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('pages:minutePacks.fields.minutesQuantity')}
              value={form.minutesQuantity}
              onChange={(event) => setForm((prev) => ({ ...prev, minutesQuantity: event.target.value }))}
              fullWidth
            />
            <TextField
              label={t('pages:minutePacks.fields.price')}
              value={form.priceAmount}
              onChange={(event) => setForm((prev) => ({ ...prev, priceAmount: event.target.value }))}
              fullWidth
              helperText={formatCurrency(Number(form.priceAmount || 0), form.priceCurrency, 2)}
            />
            <FormControl fullWidth>
              <InputLabel>{t('pages:minutePacks.fields.currency')}</InputLabel>
              <Select
                value={form.priceCurrency}
                label={t('pages:minutePacks.fields.currency')}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    priceCurrency: event.target.value as CurrencyCode,
                  }))
                }
              >
                {SUPPORTED_CURRENCY_CODES.map((currencyCode) => (
                  <MenuItem key={currencyCode} value={currencyCode}>
                    {currencyCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label={t('pages:minutePacks.fields.isActive')}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
